import type { EditorElement } from '@wix/component-protocol/schema';
import type { CompRef } from '@wix/document-services-types';
import { SYSTEM_CONTAINER_TYPE } from '../constants/componentTypes';
import { ManifestComponentExtensionType } from '../stores';
import { AbstractManifestMiddleware } from './AbstractManifestMiddleware';
import { ContainerPlatformService } from '../componentsPlatformServices';
import {
  SimpleContainerOverride,
  TemplateContainerOverride,
  MenuItemsContainerOverride,
} from './containerOverrides';
import type { AbstractInternalContainerOverride } from './containerOverrides/AbstractInternalContainerOverride';

import {
  EditorPointer,
  EditorPointerServiceAPI,
} from '@wix/editor-document-logic-api';

export class InternalSystemContainerMiddleware extends AbstractManifestMiddleware {
  #containerPlatformService: ContainerPlatformService;
  #overrides: AbstractInternalContainerOverride[];

  constructor(
    editorPointerAPI: EditorPointerServiceAPI,
    containerPlatformService: ContainerPlatformService,
  ) {
    super(editorPointerAPI);
    this.#containerPlatformService = containerPlatformService;
    this.#overrides = [
      new SimpleContainerOverride(containerPlatformService),
      new TemplateContainerOverride(containerPlatformService),
      new MenuItemsContainerOverride(containerPlatformService),
    ];
  }

  #overrideFromParentManifest(
    editorPointer: EditorPointer<CompRef>,
    editorElement: EditorElement,
  ): EditorElement | null {
    const containerOwnerPointer =
      this.#containerPlatformService.getContainerOwnerPointer(editorPointer);

    if (!containerOwnerPointer) {
      return null;
    }

    for (const override of this.#overrides) {
      const result = override.resolve(
        editorPointer,
        containerOwnerPointer,
        editorElement,
      );
      if (result !== null) {
        return result;
      }
    }

    return null;
  }

  resolveRootElement: AbstractManifestMiddleware['resolveRootElement'] = (
    node,
  ) => {
    const manifest = node.getManifest();

    if (!manifest) {
      return null;
    }

    const editorPointer = node.getEditorPointer();

    if (node.getExtensionType() === ManifestComponentExtensionType.BUILDER) {
      const componentType = node.getComponentType();

      if (componentType !== SYSTEM_CONTAINER_TYPE) {
        return null;
      }

      const overrides = this.#overrideFromParentManifest(
        editorPointer,
        manifest.editorElement!,
      );

      return overrides
        ? {
            editorElement: overrides,
          }
        : null;
    }

    return null;
  };

  resolveChildRefElement: AbstractManifestMiddleware['resolveChildRefElement'] =
    () => {
      return null;
    };
}
